<script lang="ts">
  import { get } from "svelte/store";
  import { createEventDispatcher } from 'svelte';
  import { Card, Badge, Button } from "flowbite-svelte";
  import { navigateTo } from "../../services/routing/route-action.js";
  import { type Project } from "../../services/api/types.js";
  import { editObject, drawerStore } from "../../services/stores/object-drawer-store.js";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faPencil, faEye } from "@fortawesome/free-solid-svg-icons";
  import { userStore } from "../../services/stores/user-store.js";
  import DocumentTree from "../content/DocumentTree.svelte";

  export let project: Project;

  const dispatch = createEventDispatcher<{
    fullWorksheet: void;
    documentSelect: { documentId: string; documentTitle: string; documentUrl: string; studyName: string };
  }>();

  let canManageProjects = false;
  const store = get(userStore);
  canManageProjects = store.claims?.canManageProjects ?? false;

  // Hardcoded document tree with the 5 documents
  const documentTreeData = [
    {
      label: "EVEREST",
      children: [
        {
          label: "Journal of Thorasic Oncology 2014",
          link: "#",
          documentId: "cBpU95gBW4DRgsiT_3cn",
          id: "cBpU95gBW4DRgsiT_3cn"
        },
        {
          label: "Journal of Cancer",
          link: "#",
          documentId: "zhpV95gBW4DRgsiTd3ft",
          id: "zhpV95gBW4DRgsiTd3ft"
        }
      ]
    },
    {
      label: "MATTERHORN",
      children: [
        {
          label: "Journal of Clinical Oncology",
          link: "#",
          documentId: "yRpV95gBW4DRgsiTb3fw",
          id: "yRpV95gBW4DRgsiTb3fw"
        },
        {
          label: "Japanese Journal of Oncology 2000",
          link: "#",
          documentId: "ixpV95gBW4DRgsiTFndZ",
          id: "ixpV95gBW4DRgsiTFndZ"
        },
        {
          label: "Cancer Journal 2000",
          link: "#",
          documentId: "aBpU95gBW4DRgsiT9Hd_",
          id: "aBpU95gBW4DRgsiT9Hd_"
        }
      ]
    }
  ];

  // Document info map for lookup (includes study name/group)
  const documentsMap = new Map<string, { documentId: string; documentTitle: string; documentUrl: string; studyName: string }>([
    ["cBpU95gBW4DRgsiT_3cn", {
      documentId: "cBpU95gBW4DRgsiT_3cn",
      documentTitle: "Journal of Thorasic Oncology 2014",
      documentUrl: "/pdf/cBpU95gBW4DRgsiT_3cn.pdf",
      studyName: "EVEREST"
    }],
    ["zhpV95gBW4DRgsiTd3ft", {
      documentId: "zhpV95gBW4DRgsiTd3ft",
      documentTitle: "Journal of Cancer",
      documentUrl: "/pdf/zhpV95gBW4DRgsiTd3ft.pdf",
      studyName: "EVEREST"
    }],
    ["yRpV95gBW4DRgsiTb3fw", {
      documentId: "yRpV95gBW4DRgsiTb3fw",
      documentTitle: "Journal of Clinical Oncology",
      documentUrl: "/pdf/yRpV95gBW4DRgsiTb3fw.pdf",
      studyName: "MATTERHORN"
    }],
    ["ixpV95gBW4DRgsiTFndZ", {
      documentId: "ixpV95gBW4DRgsiTFndZ",
      documentTitle: "Japanese Journal of Oncology 2000",
      documentUrl: "/pdf/ixpV95gBW4DRgsiTFndZ.pdf",
      studyName: "MATTERHORN"
    }],
    ["aBpU95gBW4DRgsiT9Hd_", {
      documentId: "aBpU95gBW4DRgsiT9Hd_",
      documentTitle: "Cancer Journal 2000",
      documentUrl: "/pdf/aBpU95gBW4DRgsiT9Hd_.pdf",
      studyName: "MATTERHORN"
    }]
  ]);

  $: {
    if (
      $drawerStore.objectType === "project" &&
      $drawerStore.object?.project_id === project.project_id
    ) {
      project = $drawerStore.object;
    }
  }

  function onEditClick() {
    editObject("project", project.project_id);
  }

  function onViewResults() {
  }

  function onFullWorksheet() {
    dispatch('fullWorksheet');
  }

  function onDocumentClick(event: CustomEvent<{ documentId: string; documentTitle: string }>) {
    // Look up document info from map
    const docInfo = documentsMap.get(event.detail.documentId);
    if (docInfo) {
      dispatch('documentSelect', {
        documentId: docInfo.documentId,
        documentTitle: docInfo.documentTitle,
        documentUrl: docInfo.documentUrl,
        studyName: docInfo.studyName
      });
    } else {
      // Fallback if not found in map
      dispatch('documentSelect', {
        documentId: event.detail.documentId,
        documentTitle: event.detail.documentTitle,
        documentUrl: "#",
        studyName: "Study 1"
      });
    }
  }
</script>

<Card class="app-card-area h-full w-full">
  <div class="flex items-center justify-left mb-4">
    <h3 class="text-base font-bold">Project</h3>
    {#if canManageProjects}
      <Button
        pill={true}
        outline={true}
        class="general-button icon-only borderless ml-2"
        size="sm"
        on:click={onEditClick}
        >
        <FontAwesomeIcon icon={faPencil} />
      </Button>
    {/if}
  </div>
  <div class="space-y-4">
    <div>
      <h4 class="card-label-text">Name</h4>
      <p class="card-text">{project.name}</p>
    </div>
    <div>
      <h4 class="card-label-text">Therapeutic Area</h4>
      <p class="card-text">{project.metadata.therapeutic_area.name}</p>
    </div>
    <div>
      <h4 class="card-label-text">Update</h4>
      <div>  
        <span style="display:inline-block; width:14px; height:14px; background:#b6e3c6; border-radius:3px; vertical-align:middle; margin-right:6px;"></span>   
        <span class="text-xs font-medium card-text">Update 4</span>
        <span class="ml-2 text-xs text-gray-400 tracking-widest">MAY-2025</span>
      </div>   
    </div>
    <div>
      <h4 class="card-label-text">Stage</h4>
      <div>
        <span class="update-stage">Extract</span>
        <span class="update-stage-status ml-2">In Progress</span>
      </div>
    </div>

    <div>
      <a href="javascript:void(0)"  class="doc-link full-worksheet-link" on:click|preventDefault={onFullWorksheet}>
        FULL WORKSHEET
      </a>
    </div>

    <div>
      <h4 class="card-label-text">Documents</h4>
      <div class="app-field readonly">
        <DocumentTree {documentTreeData} on:documentClick={onDocumentClick} />
      </div>
    </div>

  </div>
</Card>
